import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Api } from '../api/axiosInstance';
import { validatePdfFileBytes } from '../../utils/fileValidation';

// ── Types ────────────────────────────────────────────────────────────────────

interface BulkFileResult {
    filename: string;
    status: 'queued' | 'processing' | 'success' | 'error' | 'duplicate';
    message?: string;
    candidateId?: number;
}

interface BulkJobStatus {
    jobId: string;
    status: 'processing' | 'completed';
    total: number;
    completed: number;
    successful: number;
    errors: number;
    duplicates: number;
    results: BulkFileResult[];
}

interface ResumeUploadState {
    resumeData: any[];
    parsedResume: any | null;
    loading: boolean;
    error: string | null;
    success: boolean;
    // Polling state
    jobId: string | null;
    jobStatus: BulkJobStatus | null;
}

// ── Thunks ───────────────────────────────────────────────────────────────────

/** Step 1: Upload files → backend returns { jobId } immediately */
export const startBulkUpload = createAsyncThunk<
    { jobId: string },
    { resumes: File[]; job_id?: string },
    { rejectValue: string }
>('resume/startBulkUpload', async ({ resumes, job_id }, { rejectWithValue }) => {
    try {
        if (resumes.length > 20) {
            return rejectWithValue('Maximum 20 resumes allowed per upload.');
        }

        for (const file of resumes) {
            const result = await validatePdfFileBytes(file);
            if (result !== true) {
                return rejectWithValue(`${file.name}: ${result}`);
            }
        }

        const formData = new FormData();
        resumes.forEach((file) => formData.append('resumes', file));
        if (job_id) formData.append('job_id', job_id);

        const response = await Api.post('/resume-parser/upload-bulk', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });

        const data = response.data?.data ?? response.data;
        return { jobId: data.jobId };
    } catch (err: any) {
        if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
            return rejectWithValue('Request timed out. Please try again.');
        }
        if (err.response?.status === 413) {
            return rejectWithValue('File too large. Maximum size is 10MB per file.');
        }
        const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to upload resumes';
        return rejectWithValue(errorMsg);
    }
});

/** Step 2: Poll job status until completed */
export const pollJobStatus = createAsyncThunk<
    BulkJobStatus,
    string,
    { rejectValue: string }
>('resume/pollJobStatus', async (jobId, { rejectWithValue }) => {
    try {
        const response = await Api.get(`/resume-parser/jobs/${jobId}/status`);
        const data = response.data?.data ?? response.data;
        return data as BulkJobStatus;
    } catch (err: any) {
        const errorMsg = err.response?.data?.message || err.message || 'Failed to fetch job status';
        return rejectWithValue(errorMsg);
    }
});

/** Single resume upload → returns { jobId } for polling (same pattern as bulk) */
export const startSingleUpload = createAsyncThunk<
    { jobId: string },
    { resume: File; job_id?: string },
    { rejectValue: string }
>('resume/startSingleUpload', async ({ resume, job_id }, { rejectWithValue }) => {
    try {
        const result = await validatePdfFileBytes(resume);
        if (result !== true) {
            return rejectWithValue(`${resume.name}: ${result}`);
        }

        const formData = new FormData();
        formData.append('resume', resume);
        if (job_id) formData.append('job_id', job_id);

        const response = await Api.post('/resume-parser/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });

        const data = response.data?.data ?? response.data;
        return { jobId: data.jobId };
    } catch (err: any) {
        if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
            return rejectWithValue('Request timed out. Please try again.');
        }
        if (err.response?.status === 413) {
            return rejectWithValue('File too large (max 10MB)');
        }
        const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'Upload failed';
        return rejectWithValue(errorMsg);
    }
});

// ── Slice ────────────────────────────────────────────────────────────────────

const initialState: ResumeUploadState = {
    resumeData: [],
    parsedResume: null,
    loading: false,
    error: null,
    success: false,
    jobId: null,
    jobStatus: null,
};

const resumeUploadSlice = createSlice({
    name: 'resumeUpload',
    initialState,
    reducers: {
        resetResumeUploadState: (state) => {
            state.resumeData = [];
            state.parsedResume = null;
            state.loading = false;
            state.error = null;
            state.success = false;
            state.jobId = null;
            state.jobStatus = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Start bulk upload
            .addCase(startBulkUpload.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
                state.jobId = null;
                state.jobStatus = null;
            })
            .addCase(startBulkUpload.fulfilled, (state, action) => {
                state.jobId = action.payload.jobId;
                // loading stays true — we're now polling
            })
            .addCase(startBulkUpload.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to start upload';
            })
            // Poll job status
            .addCase(pollJobStatus.fulfilled, (state, action) => {
                state.jobStatus = action.payload;
                if (action.payload.status === 'completed') {
                    state.loading = false;
                    state.success = true;
                    state.resumeData = action.payload.results;
                }
            })
            .addCase(pollJobStatus.rejected, (state, action) => {
                state.error = action.payload || 'Failed to fetch status';
                // Don't stop loading — let the UI retry polling
            })
            // Single upload (polling-based, same pattern as bulk)
            .addCase(startSingleUpload.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
                state.jobId = null;
                state.jobStatus = null;
            })
            .addCase(startSingleUpload.fulfilled, (state, action) => {
                state.jobId = action.payload.jobId;
                // loading stays true — we're now polling
            })
            .addCase(startSingleUpload.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Resume upload failed';
            });
    },
});

export const { resetResumeUploadState } = resumeUploadSlice.actions;
export default resumeUploadSlice.reducer;
